import React from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";
import { useAuth } from "../context/AuthContext";

const ExportToCSVButton = ({
  columnsConfig,
  columns,
  theme,
  globalFilter,
  formatCurrency,
  formatNumber,
  data,
  filename,
}) => {
  const { claims } = useAuth();
  const exportEnabled = claims?.roles?.includes("role.export");
  const handleExportToCSV = () => {
    // Determine columns based on whether columnsConfig or columns is provided
    let headers, accessors, columnConfigs;
    if (columnsConfig) {
      const config = columnsConfig(
        theme,
        globalFilter,
        formatCurrency,
        formatNumber,
      );
      headers = config.map((col) => {
        // Handle header as string or function
        if (typeof col.header === "string") {
          return col.header;
        }
        if (typeof col.header === "function") {
          // Try to get header text from function result or use accessorKey
          try {
            const headerResult = col.header();
            if (typeof headerResult === "string") {
              return headerResult;
            }
          } catch (e) {
            // If function fails, use accessorKey
          }
        }
        return col.accessorKey || "";
      });
      accessors = config.map((col) => col.accessorKey);
      columnConfigs = config;
    } else {
      headers = columns;
      accessors = columns;
      columnConfigs = [];
    }

    // Helper function to get raw value from row, handling custom Cell renderers
    const getRawValue = (row, accessorKey, columnConfig) => {
      // A column can supply getExportValue to make the export match exactly
      // what its own Cell displays (e.g. historicalTrades.js's archive_date,
      // which can differ from the raw field) — check this before falling
      // back to the raw row value.
      if (columnConfig && typeof columnConfig.getExportValue === "function") {
        try {
          const exportValue = columnConfig.getExportValue(row);
          if (exportValue !== null && exportValue !== undefined) {
            return exportValue;
          }
        } catch (e) {
          // Fall through to the raw value below.
        }
      }

      let value = row[accessorKey];

      // Handle null/undefined
      if (value === null || value === undefined || value === "") {
        return "";
      }

      // maturity is a plain "YYYY-MM-DD" business date, same as every other
      // date column here — pass it through as-is rather than round-tripping
      // through `new Date(...)`, which parses a date-only string as UTC
      // midnight and would then render a day off in any timezone west of
      // UTC once reformatted via toLocaleDateString().
      if (accessorKey === "maturity" && typeof value === "string") {
        return value.split("T")[0];
      }

      // For numeric values, return the number (not formatted)
      if (typeof value === "number") {
        return value;
      }

      // Convert string numbers to numbers if they're numeric
      if (typeof value === "string") {
        const numValue = Number(value);
        if (!isNaN(numValue) && value.trim() !== "") {
          return numValue;
        }
      }

      return value;
    };

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        accessors
          .map((key, index) => {
            const columnConfig = columnConfigs[index];
            const value = getRawValue(row, key, columnConfig);
            
            if (value === null || value === undefined || value === "") {
              return "";
            }
            
            // Convert to string and handle commas
            const stringValue = String(value);
            if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            
            return stringValue;
          })
          .join(","),
      ),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${filename.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return exportEnabled ? (
    <Button
      variant="contained"
      startIcon={<DownloadIcon />}
      onClick={handleExportToCSV}
      sx={{
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
        borderRadius: theme.shape.borderRadius,
        "& .MuiButton-startIcon": {
          margin: `0 ${theme.spacing(1)} 0 ${theme.spacing(-0.5)}`,
        },
      }}
    >
      <Typography sx={{ display: { xs: "none", sm: "inline" } }}>
        Export to CSV
      </Typography>
    </Button>
  ) : null;
};

ExportToCSVButton.propTypes = {
  columnsConfig: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.string),
  theme: PropTypes.object,
  globalFilter: PropTypes.string,
  formatCurrency: PropTypes.func,
  formatNumber: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  filename: PropTypes.string.isRequired,
};

ExportToCSVButton.defaultProps = {
  columnsConfig: null,
  columns: null,
  theme: {},
  globalFilter: "",
  formatCurrency: (value) => value,
  formatNumber: (value) => value,
};

export default ExportToCSVButton;
